/**
 * O de-para entre o texto da inconsistência e o campo que ela torna editável.
 *
 * A `INCONSISTENCIAS` guarda o achado como texto livre na coluna `INCONSISTENCIA`, então é por
 * ele que se descobre qual campo daquela pessoa está divergente. Achado que não aparece neste
 * mapa é de **presença** (a pessoa existe de um lado e não do outro) e não se resolve editando
 * célula — exige inclusão ou exclusão.
 */
export type CampoEditavel =
  | "nome"
  | "dataNascimento"
  | "sexo"
  | "dataAdmissao"
  | "plano"
  | "vigenciaInicial"
  | "vigenciaFinal"
  | "valorPlano"
  | "grauParentesco"
  | "cpfTitular";

/**
 * Casado por trecho, não por igualdade: as strings do bate de origem e do bate da operadora
 * descrevem o mesmo campo com frases diferentes ("Nomes diferentes nos dados cadastrais e posição
 * cadastral" contra "Nomes diferentes na Conducco e na posição da operadora").
 */
export const INCONSISTENCIA_PARA_CAMPO: Array<{ trecho: string; campo: CampoEditavel }> = [
  { trecho: "nomes diferentes", campo: "nome" },
  { trecho: "datas de nascimento", campo: "dataNascimento" },
  { trecho: "sexos diferentes", campo: "sexo" },
  { trecho: "datas de contratação", campo: "dataAdmissao" },
  { trecho: "planos diferentes", campo: "plano" },
  /**
   * "Coberta sem carteirinha emitida" não entra: não há onde gravar o número do cartão do nosso
   * lado. `EMPLOYEE_REGISTRATION_NUMBER` é a única coluna do gênero nas duas massas e já carrega
   * a matrícula que a carga operadora grava. O achado fica como registro, sem retificação.
   */
  { trecho: "início de vigência", campo: "vigenciaInicial" },
  { trecho: "fim de vigência", campo: "vigenciaFinal" },
  { trecho: "exclusão aprovada e não executada", campo: "vigenciaFinal" },
  { trecho: "preço diferente", campo: "valorPlano" },
  { trecho: "tipo de vínculo", campo: "grauParentesco" },
  { trecho: "cpf do titular", campo: "cpfTitular" },
];

export function campoDaInconsistencia(inconsistencia: string): CampoEditavel | null {
  const texto = inconsistencia.toLowerCase();

  return INCONSISTENCIA_PARA_CAMPO.find(({ trecho }) => texto.includes(trecho))?.campo ?? null;
}

/** Rótulo de coluna por campo, para a grade e para o resumo de pendências. */
export const LABEL_CAMPO: Record<CampoEditavel, string> = {
  nome: "Nome",
  dataNascimento: "Nascimento",
  sexo: "Sexo",
  dataAdmissao: "Admissão",
  plano: "Plano",
  vigenciaInicial: "Início da vigência",
  vigenciaFinal: "Fim da vigência",
  valorPlano: "Valor do plano",
  grauParentesco: "Grau de parentesco",
  cpfTitular: "CPF do titular",
};

/** Uma linha da grade: uma pessoa, com o valor de cada campo e quais deles divergem. */
export type LinhaRetificacao = {
  id: string;
  cpf: string;
  nome: string;
  tipo: "employee" | "dependent";
  /** Quais campos desta pessoa estão divergentes. Só estes são editáveis. */
  camposDivergentes: CampoEditavel[];
  /** Achados de presença, que não se resolvem editando célula. */
  achadosDePresenca: string[];
  /**
   * O valor da **nossa** base, por campo. É este que a célula mostra e que se edita, porque é
   * ele que está errado: a posição da operadora é o fato executado.
   */
  valores: Partial<Record<CampoEditavel, string | null>>;
  /** O valor da operadora, por campo. Referência do que corrigir para, exibido no tooltip. */
  valoresOperadora: Partial<Record<CampoEditavel, string | null>>;
  /**
   * O valor como veio do backend, por campo. É a referência para saber se a célula foi editada:
   * enquanto igual ao original, a pendência continua aberta e a célula fica vermelha.
   */
  valoresOriginais: Partial<Record<CampoEditavel, string | null>>;
};

/** O que será enviado ao backend quando ele existir. */
export type RetificacaoPendente = {
  cpf: string;
  campo: CampoEditavel;
  valorAnterior: string | null;
  valorNovo: string | null;
};

/** Retorno de `PATCH /v2/bate-cadastral/:workspaceActivityId/retificar`. */
export type RetificarResposta = {
  pessoas: number;
  campos: number;
  /** Quantas linhas cada massa teve afetada. Zero em uma delas é sinal de recorte errado. */
  linhasAfetadas: { elegivel: number; movimentada: number };
  /** Correções gravadas na camada que sobrevive à reconstrução da massa. */
  persistidas: { atualizadas: number; criadas: number };
  ignoradas: Array<{ cpf: string; campo?: string; motivo: string }>;
};
